import { useQuery } from '@tanstack/react-query';
import { fetchTermsStatus } from '@/lib/api';
import { useViewport } from '@/hooks/useViewport';
import { initialSiteSelection, siteDetailPath, spreadSiteMarkers } from '@/lib/siteDashboard';
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { AnimatePresence } from 'framer-motion';
import Supercluster from 'supercluster';
import { useDashboardMapPoints } from '@/hooks/useDashboardMapPoints';
import { normalizeLatLng } from '@/lib/coords';
import { LocationInfoCard, type InfoCardLocation } from '@/components/LocationInfoCard';
import { MapContainer } from './widgets/_lib/MapContainer';
import { useMap } from './widgets/_lib/useMap';
import { LocationMarker, ClusterMarker } from './widgets/_lib/LocationMarker';
import { CameraController } from './widgets/_lib/CameraController';
import styles from './DashboardPage.module.css';

/**
 * Phase 7g.4b.2.5 — InfoCardComponent prop contract. The card
 * receives the selected location's identity + its open/close +
 * detail-open handlers. SP variant renders different KPIs (chargers
 * + active sessions) while sharing the marker scene + camera +
 * cluster logic. Operator caller passes nothing and gets the
 * existing LocationInfoCard via the default below.
 */
export interface DashboardMapInfoCardProps {
  location: InfoCardLocation;
  onClose: () => void;
  onOpenDetail: (id: string) => void;
}

/**
 * Full-bleed dashboard map background. Replaces the previous
 * widget-chrome'd DestinationMapWidget — the map IS the
 * dashboard background now, not one tile inside the grid.
 *
 * Owns the same scene the widget did (MapContainer, MarkerLayer,
 * CameraController, AnimatePresence + LocationInfoCard) but
 * without the header / count / mapFallback chrome. The widget
 * version was deleted; this is its replacement.
 *
 * Stacks at z-index 0 inside the dashboard's relative root, so
 * the .page header and .grid above can render at z-index ≥ 2
 * without competing with the map's own internal canvas + marker
 * elements. The info card sits at .card's own z-index 8 (set in
 * LocationInfoCard.module.css) which lifts it above the widget
 * grid; that's intentional — the card is a focused decision
 * surface and should win over ambient widgets while open.
 *
 * Empty + missing-token states are flat fallbacks (no map canvas
 * mounted) — the MapContainer itself handles the no-token case
 * via a render-time guard.
 */
export function DashboardMapBackground({
  onNavigate,
  mode = 'dark',
  siteView = false,
  fitToPoints = false,
  InfoCardComponent = LocationInfoCard,
}: {
  onNavigate?: (to: string) => void;
  /** Active theme mode — forwarded to MapContainer so it can pick
   *  the appropriate basemap style URL. Defaults to 'dark' for any
   *  caller that doesn't know about themes (no PWA today, but the
   *  default keeps the existing behavior). */
  mode?: 'light' | 'dark';
  siteView?: boolean;
  fitToPoints?: boolean;
  /** Phase 7g.4b.2.5 — info-card component slot. Defaults to the
   *  operator-flavored LocationInfoCard (Revenue Today / Energy MTD
   *  / Chargers Online / Active Sessions KPI grid). SP dashboard
   *  passes its own SalesPartnerLocationInfoCard with SP-flavored
   *  stats (Chargers Online / Active Sessions only in v1; the
   *  per-location commission breakdown lands in Phase 7g.4b.3 with
   *  the agent-payouts projection extension). Card lifecycle
   *  (open/close, AnimatePresence) stays here so the marker scene
   *  + camera + cluster logic don't need to be duplicated. */
  InfoCardComponent?: ComponentType<DashboardMapInfoCardProps>;
} = {}) {
  const q = useDashboardMapPoints();
  const data = q.data;
  const viewport = useViewport();
  const initialSelectionDone = useRef(false);
  const terms = useQuery({
    queryKey: ['terms', 'status', 'applicable'],
    queryFn: () => fetchTermsStatus(),
    enabled: siteView,
    staleTime: 5 * 60_000,
  });
  const ready = !siteView || (!!terms.data && !terms.isError && terms.data.every((doc) => doc.accepted && !doc.unavailable));

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const points = useMemo<MapPointInternal[]>(() => {
    if (!data) return [];
    const out: MapPointInternal[] = [];
    for (const row of data.rows) {
      if (!row.coordinates) continue;
      // Defensive: recover from the decimal-stripped-longitude
      // data bug observed in the PWA (lng stored as
      // -7987638282685921 instead of -79.87638282685921). See
      // MapPage.tsx for the write-path caveat.
      const norm = normalizeLatLng(row.coordinates, `admin location ${row._id}`);
      if (!norm) continue;
      out.push({
        _id: row._id,
        name: row.name,
        chargerCount: row.charger_count,
        onlineChargerCount: row.online_chargers_count,
        lat: norm.lat,
        lng: norm.lng,
      });
    }
    return out;
  }, [data]);

  useEffect(() => {
    if (!siteView || !ready || !data || initialSelectionDone.current) return;
    initialSelectionDone.current = true;
    setSelectedId(initialSiteSelection(points.map((p) => p._id), viewport !== 'desktop'));
  }, [siteView, ready, data, points, viewport]);

  const selected = useMemo<MapPointInternal | null>(
    () => (selectedId ? points.find((p) => p._id === selectedId) ?? null : null),
    [selectedId, points],
  );

  const cardLocation = useMemo<InfoCardLocation | null>(() => {
    if (!selected) return null;
    return {
      _id: selected._id,
      name: selected.name,
      chargerCount: selected.chargerCount,
      onlineChargerCount: selected.onlineChargerCount,
    };
  }, [selected]);

  return (
    <>
      <MapContainer
        className={styles.mapBackgroundCanvas}
        mode={mode}
      >
        <MarkerLayer
          points={points}
          siteView={siteView}
          selectedId={selectedId}
          onSelect={(loc) => setSelectedId(loc._id)}
        />
        <CameraController selected={selected} points={points} siteView={siteView || fitToPoints} ready={ready} />
      </MapContainer>

      <AnimatePresence>
        {ready && cardLocation && (
          <InfoCardComponent
            key={cardLocation._id}
            location={cardLocation}
            onClose={() => setSelectedId(null)}
            onOpenDetail={(id) => {
              const to = siteView ? siteDetailPath(id) : `/locations/${id}`;
              if (onNavigate) onNavigate(to);
              else window.location.href = to;
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

interface MapPointInternal {
  _id: string;
  name: string;
  chargerCount: number;
  onlineChargerCount: number;
  lat: number;
  lng: number;
}

const CLUSTER_RADIUS_PX = 60;
const CLUSTER_MAX_ZOOM = 16;
/**
 * Markers are clustered over the whole world at the current zoom, never
 * over map.getBounds(). Once the camera has flown with padding, getBounds()
 * returns only the padded inset, and with the globe projection only an
 * approximation of the visible region, so a location just outside that box
 * lost its marker while it was still on screen: the Sales Partner marker
 * vanished on a slight pan or zoom out (Warren, 20 October 2026). Markers
 * off screen cost nothing; Mapbox places them outside the canvas.
 */
const WORLD_BBOX: [number, number, number, number] = [-180, -85, 180, 85];

/**
 * Watches the map's zoom + bounds, clusters every point at the
 * current zoom (WORLD_BBOX: the bounds only re-run the site-view
 * marker spread on a move), and renders <LocationMarker> per individual
 * point + <ClusterMarker> per cluster. Lives inside MapContainer
 * so it has access to the mapbox-gl instance via MapContext.
 *
 * Re-clusters on `moveend` + `zoomend` only (not `move`) to keep
 * inertia pans cheap; supercluster's index lookup is O(log n)
 * but the marker remount cycle for 50+ pins still costs frames
 * when fired per-frame.
 */
function MarkerLayer({
  points, selectedId, onSelect, siteView,
}: {
  points: MapPointInternal[];
  siteView: boolean;
  selectedId: string | null;
  onSelect: (p: MapPointInternal) => void;
}) {
  const { map, isLoaded } = useMap();
  const [camera, setCamera] = useState<{ zoom: number; bbox: [number, number, number, number] }>({
    zoom: 4,
    bbox: [-180, -85, 180, 85],
  });

  const cluster = useMemo(() => {
    const sc = new Supercluster<{ mapPoint: MapPointInternal }, { chargerSum: number }>({
      radius: CLUSTER_RADIUS_PX,
      maxZoom: CLUSTER_MAX_ZOOM,
      map: (props) => ({ chargerSum: props.mapPoint.chargerCount }),
      reduce: (accumulated, props) => {
        accumulated.chargerSum += props.chargerSum;
      },
    });
    sc.load(
      points.map((p) => ({
        type: 'Feature' as const,
        properties: { mapPoint: p },
        geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
      })),
    );
    return sc;
  }, [points]);

  useEffect(() => {
    if (!map || !isLoaded) return;
    const update = () => {
      const b = map.getBounds();
      const z = map.getZoom();
      if (!b) return;
      setCamera({
        zoom: Math.round(z),
        bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
      });
    };
    update();
    map.on('moveend', update);
    map.on('zoomend', update);
    return () => {
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map, isLoaded]);

  const clusters = useMemo(
    () => cluster.getClusters(WORLD_BBOX, camera.zoom),
    [cluster, camera.zoom],
  );

  const handleClusterClick = useCallback((clusterId: number, lng: number, lat: number) => {
    if (!map) return;
    const nextZoom = cluster.getClusterExpansionZoom(clusterId);
    map.flyTo({
      center: [lng, lat],
      zoom: Math.min(nextZoom, CLUSTER_MAX_ZOOM),
      duration: 1000,
      essential: false,
    });
  }, [map, cluster]);

  const offsets = useMemo(() => {
    if (!siteView || !map || !isLoaded) return new Map<string, [number, number]>();
    return spreadSiteMarkers(points.map((p) => {
      const screen = map.project([p.lng, p.lat]);
      return { id: p._id, x: screen.x, y: screen.y };
    }));
  }, [siteView, map, isLoaded, points, camera]);

  if (siteView) return <>{points.map((point) => (
    <LocationMarker key={point._id} location={point} selected={selectedId === point._id}
      offset={offsets.get(point._id)} onSelect={onSelect} />
  ))}</>;

  return (
    <>
      {clusters.map((c) => {
        const [lng, lat] = c.geometry.coordinates as [number, number];
        const isCluster = (c.properties as { cluster?: boolean }).cluster === true;

        if (isCluster) {
          const clusterProps = c.properties as { cluster_id: number; chargerSum: number };
          return (
            <ClusterMarker
              key={`cluster-${clusterProps.cluster_id}-${camera.zoom}`}
              clusterId={clusterProps.cluster_id}
              lng={lng}
              lat={lat}
              chargerSum={clusterProps.chargerSum}
              onClick={handleClusterClick}
            />
          );
        }

        const { mapPoint } = c.properties as { mapPoint: MapPointInternal };
        return (
          <LocationMarker
            key={`loc-${mapPoint._id}`}
            location={mapPoint}
            selected={selectedId === mapPoint._id}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
}
